import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { OAuthModule } from 'angular-oauth2-oidc';
import { fr } from 'date-fns/locale';
import { ComboComponent } from 'app/shared/components/combo/combo.component';
import { MAT_DATE_FNS_DATE_FORMATS_FR } from 'app/shared/mat-date-fns/date-fns-date-formats';
import { LaboratoryDateFnsDateAdapter } from 'app/shared/mat-date-fns/laboratory-date-fns-date-adapter';
import { CurrencySymbolPipe } from 'app/shared/pipes/currency-symbol.pipe';
import { CustomDatePipe } from 'app/shared/pipes/custom-date.pipe';
import { PipeModule } from 'app/shared/pipes/pipe.module';
import { SavedChangeGuard } from 'app/shared/saved-change/saved-change.guard';
import { apiInterceptorProvider } from './api.interceptor';
import { authInterceptorProvider } from './auth.interceptor';
import { blobErrorHttpInterceptorProvider } from './blob-error-http.interceptor';
import { loadingStatusInterceptorProvider } from './loading-status.interceptor';
import { AuthGuardWithForcedLogin } from './services/auth-guard-with-forced-login.service';
import { AuthService } from './services/auth.service';
import { AbstractActionConfirmationDialogComponent } from './services/dialog-box/abstract-action-confirmation-dialog/abstract-action-confirmation-dialog.component';
import { ActionConfirmationComboDialogComponent } from './services/dialog-box/action-confirmation-combo-dialog/action-confirmation-combo-dialog.component';
import { ActionConfirmationDialogComponent } from './services/dialog-box/action-confirmation-dialog/action-confirmation-dialog.component';
import { ActionConfirmationInformationDialogComponent } from './services/dialog-box/action-confirmation-information-dialog/action-confirmation-information-dialog.component';
import { ActionConfirmationMessageDialogComponent } from './services/dialog-box/action-confirmation-message-dialog/action-confirmation-message-dialog.component';
import { ActionConfirmationNumberDialogComponent } from './services/dialog-box/action-confirmation-number-dialog/action-confirmation-number-dialog.component';
import { SmartSnackComponent } from './services/notify/smart-snack/smart-snack.component';
import { ProgressBarComponent } from './services/progress-bar/progress-bar/progress-bar.component';
import { VersionCheckInterceptorProvider } from './version-check.interceptor';

@NgModule({
  exports: [ProgressBarComponent],
  imports: [
    ComboComponent,
    CommonModule,
    OAuthModule.forRoot(),
    MatButtonModule,
    MatProgressBarModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSlideToggleModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    PipeModule,
    ProgressBarComponent,
    ReactiveFormsModule,
    SmartSnackComponent,
    AbstractActionConfirmationDialogComponent,
    ActionConfirmationComboDialogComponent,
    ActionConfirmationDialogComponent,
    ActionConfirmationInformationDialogComponent,
    ActionConfirmationMessageDialogComponent,
    ActionConfirmationNumberDialogComponent,
  ],
  providers: [
    CustomDatePipe,
    CurrencySymbolPipe,
    { provide: DateAdapter, useClass: LaboratoryDateFnsDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_DATE_FORMATS_FR },
    { provide: MAT_DATE_LOCALE, useValue: fr },
  ],
})
export class CoreModule {
  public constructor() {
    const parentModule = inject(CoreModule, { optional: true, skipSelf: true });

    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import it in the AppModule only');
    }
  }

  public static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        VersionCheckInterceptorProvider,
        apiInterceptorProvider,
        authInterceptorProvider,
        loadingStatusInterceptorProvider,
        // /!\ THIS SHOULD COME AFTER apiInterceptorProvider BECAUSE IT NEEDS TO BE EXECUTED BEFORE apiInterceptorProvider /!\
        blobErrorHttpInterceptorProvider,
        AuthGuardWithForcedLogin,
        AuthService,
        SavedChangeGuard,
      ],
    };
  }
}
